import {
  Accordion as MuiAccordion,
  AccordionDetails,
  AccordionSummary,
  Chip,
  Divider,
  Stack,
  styled,
  SxProps,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import OrderRecapConfig from "../OrderRecapConfig/order-recap-config";

import BuildCircleOutlinedIcon from "@mui/icons-material/BuildCircleOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { AclTypes } from "../../../../Core/Api/Types/acl-types";
import { CurrencyTypes } from "../../../../Core/Api/Types/currency-types";
import { DbCustomerInfos } from "../../../../Core/Api/Types/db-customer-infos";
import { BasketGroupedItems } from "../../../../Pages/Basket/basket";
import { UserHasAcl } from "../../../../Utils/helpers";
import OrderMissingAssetCard from "../../OrderMissingAssetCard/order-missing-asset-card";
import { useTranslation } from "react-i18next";

export type OrderRecapAccordionProps = {
  className?: string;
  sx?: SxProps;
  layeredItems: BasketGroupedItems;
  customers: DbCustomerInfos[];
};

const OrderRecapAccordion: React.FC<OrderRecapAccordionProps> = (props) => {
  const { t } = useTranslation();
  const { layeredItems, customers, ...otherProps } = props;

  const actionAttachment = UserHasAcl(AclTypes.aclOrderAttachFile);
  const actionOverridePrice = UserHasAcl(AclTypes.aclOrderOverrideItemPrice);
  const actionOverrideCustomer = UserHasAcl(AclTypes.aclOrderOverrideItemCustomer);

  const [layerPrice, layerCurrency] = useMemo(() => {
    let sum = 0;
    let currency: CurrencyTypes = CurrencyTypes.EUR;

    layeredItems.items.forEach((item) => {
      if (item.overridenPrice?.price) {
        sum += item.overridenPrice?.price;
        if (item.overridenPrice?.currency) currency = item.overridenPrice?.currency;
      } else if (item.totalPrice?.price) {
        sum += item.totalPrice?.price;
        if (item.totalPrice?.currency) currency = item.totalPrice?.currency;
      }
    });

    return [sum, currency];
  }, [layeredItems]);

  const nonDoableItems = useMemo(() => {
    return layeredItems.items.filter((x) => !x.doable);
  }, [layeredItems.items]);

  return (
    <Accordion {...otherProps}>
      <AccordionSummary>
        <Stack>
          <Typography variant="subtitle2" component="h3" fontWeight={600}>
            {layeredItems.layer}
          </Typography>
          <Stack spacing={1} direction="row" alignItems="center">
            <Typography variant="body1" component="span" color="text.tier">
              {t("card:body.configurations", { count: layeredItems.items.length })}
            </Typography>
            {nonDoableItems.length > 0 && (
              <Chip
                color="warning"
                size="small"
                variant="outlined"
                icon={<BuildCircleOutlinedIcon />}
                label={t("chip:label.configurationNotDoable", { count: nonDoableItems.length })}
              />
            )}
            {layeredItems.items.every((x) => x.doable) && (
              <Chip
                color="success"
                size="small"
                variant="outlined"
                label={t("common:doable")}
                icon={<CheckCircleOutlineIcon />}
              />
            )}
            <ExpandMoreRoundedIcon
              className="order-recap-accordion-expand"
              sx={{ color: "text.tier" }}
              fontSize="small"
            />
          </Stack>
        </Stack>
        <Typography variant="subtitle2" color="text.tier">
          {layerPrice} {layerCurrency === CurrencyTypes.EUR ? "€" : "$"}
        </Typography>
      </AccordionSummary>
      <AccordionDetails>
        {layeredItems.items.map((item, index) => (
          <React.Fragment key={index}>
            {index > 0 && <Divider sx={{ my: 3 }} />}
            <OrderRecapConfig
              item={item}
              customers={customers}
              actions={{
                overridePrice: actionOverridePrice,
                overrideCustomer: actionOverrideCustomer,
                attachment: actionAttachment,
              }}
              sx={{ mb: 0.5 }}
            />
            {!item.doable && <OrderMissingAssetCard item={item} />}
          </React.Fragment>
        ))}
      </AccordionDetails>
    </Accordion>
  );
};

const Accordion = styled(MuiAccordion)(({ theme }) => ({
  padding: 0,
  background: "none",
  "&.Mui-expanded": {
    margin: 0,
    ".order-recap-accordion-expand": {
      transform: "rotate(-180deg)",
    },
  },
  ".order-recap-accordion-expand": {
    transition: "tranform .3s",
  },
  ".MuiAccordionSummary-root": {
    padding: 0,
    minHeight: 0,
    "&.Mui-expanded": {
      margin: 0,
      minHeight: "none",
    },
  },
  ".MuiAccordionSummary-content": {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingTop: 0,
    margin: 0,
    paddingBottom: theme.spacing(1),
    "&.Mui-expanded": {
      margin: 0,
    },
  },
  ".MuiAccordionDetails-root": {
    padding: 0,
    marginTop: theme.spacing(1),
    border: "none",
  },
}));

export default OrderRecapAccordion;
